import { formatISO } from "date-fns";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { View } from "@common/types/views/views";
import { ClientsCloseOverviewView } from "@common/types/views/clientsCloseOverviewView";
import { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useCloseLiveUpdates } from "@hooks/useCloseLiveUpdates";
import {
    useClientsCloseOverviewSlimQuery,
    useClientsCloseOverviewDetailsQuery,
} from "@api/workspace.api";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { pluralize } from "@common/helpers/string";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { useClientsCloseOverviewViewStore } from "@hooks/view/useClientsCloseOverviewViewStore";
import interfaceStyles from "@components/mainInterface/MainInterface.module.scss";
import { FiltersTypes } from "@common/types/filters/general";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { ClientActionsProvider } from "@components/organizationView/clients/ClientActionsProvider";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { AddClientTrigger } from "@components/organizationView/clients/AddClientTrigger";
import { useHandleEnrollAction } from "@components/organizationView/clients/useHandleEnrolAction";
import { useOpenClientWorkspace } from "@components/organizationView/clients/useOpenClientWorkspace";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { HStack } from "@design-system/HStack/HStack";
import {
    clientsCloseOverviewViewConfig,
    ClientsCloseOverviewRow,
    mapWorkspaceToRow,
} from "./clientsCloseOverviewViewConfig";
import { useBuiltInClientsClosesViews } from "./views/BuiltInClientsClosesViews";
import styles from "./ClientsClosesPage.module.scss";

const DEFAULT_PAGE = 1;
const PAGE_SIZE = 20;
const DEFAULT_SORT = ClientsWorkOverviewSortValue.APPLIED_AT_DESC;

const availableFilters: FilterTabs[] = [
    FilterTabs.START_DATE,
    FilterTabs.TEAM_MEMBERS,
    FilterTabs.LEGAL_TYPE,
    FilterTabs.PLANS,
    FilterTabs.WHO_PAYS,
    FilterTabs.SERVICES,
];

const EMPTY_TAB_FILTERS: FiltersTypes = {};

interface Filters {
    search: string;
    serviceIds: string[];
    closeDateStart: string | undefined;
    closeDateEnd: string | undefined;
}

const EMPTY_FILTERS: Filters = {
    search: "",
    serviceIds: [],
    closeDateStart: undefined,
    closeDateEnd: undefined,
};

function omitUndefined(obj: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(obj).filter(
            ([, value]) => value !== undefined && value !== null,
        ),
    );
}

const ClientsClosesPageContent: FC = () => {
    const activeOrganization = useActiveOrganization();
    const canCreateClient = useCanPerformOrganizationAction(
        OrganizationAction.CREATE_CLIENT,
    );
    const openClientWorkspace = useOpenClientWorkspace();
    useCloseLiveUpdates();
    useHandleEnrollAction();
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [sort, setSort] = useState<ClientsWorkOverviewSortValue | undefined>(
        DEFAULT_SORT,
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [tabFilters, setTabFilters] =
        useState<FiltersTypes>(EMPTY_TAB_FILTERS);

    const handleTabFiltersChange: FiltersChangeFn = useCallback((partial) => {
        setTabFilters((prev) => ({
            ...omitUndefined({ ...prev, ...partial }),
        }));
        setPage(DEFAULT_PAGE);
    }, []);

    const { view, setView } = useClientsCloseOverviewViewStore({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.CLIENTS_CLOSES_PAGE_VIEW_CACHE_KEY,
    });
    const builtInViews = useBuiltInClientsClosesViews();

    const handleViewChange = useCallback(
        (newView: View) => {
            setView(newView as ClientsCloseOverviewView);
        },
        [setView],
    );

    const viewToolbar = useMemo(
        () => (
            <ViewToolbar
                view={view}
                onViewChange={setView}
                viewConfig={clientsCloseOverviewViewConfig}
                builtInViews={builtInViews}
                defaultFilters={{}}
                baseViewCacheKey={
                    ORGANIZATION_USER_CACHE_KEYS.CLIENTS_CLOSES_PAGE_BASE_VIEW_ID_CACHE_KEY
                }
            />
        ),
        [view, setView, builtInViews],
    );

    useSubNavigationPortalContent("clients-view-toolbar", viewToolbar);

    const handleSortChange = useCallback(
        (newSort: ClientsWorkOverviewSortValue) => {
            setSort(newSort);
            setPage(DEFAULT_PAGE);
        },
        [],
    );

    const handleSortRemove = useCallback(() => {
        // Removing the active sort returns to the page's default sort rather
        // than an unrelated order (backend resolves undefined to the same).
        setSort(DEFAULT_SORT);
        setPage(DEFAULT_PAGE);
    }, []);

    const slimParams = useMemo(
        () => ({
            organizationId: activeOrganization?.id ?? "",
            page,
            limit: PAGE_SIZE,
            sort,
            filters: {
                search: filters.search || undefined,
                serviceIds:
                    filters.serviceIds.length > 0
                        ? filters.serviceIds
                        : undefined,
                closeDateStart: filters.closeDateStart,
                closeDateEnd: filters.closeDateEnd,
                bookkeepingStartDateStart: tabFilters.bookkeepingStartDateStart
                    ? formatISO(tabFilters.bookkeepingStartDateStart, {
                          representation: "date",
                      })
                    : undefined,
                bookkeepingStartDateEnd: tabFilters.bookkeepingStartDateEnd
                    ? formatISO(tabFilters.bookkeepingStartDateEnd, {
                          representation: "date",
                      })
                    : undefined,
                teamMemberIds:
                    tabFilters.teamMemberIds &&
                    tabFilters.teamMemberIds.length > 0
                        ? tabFilters.teamMemberIds
                        : undefined,
                legalTypes:
                    tabFilters.legalTypes && tabFilters.legalTypes.length > 0
                        ? tabFilters.legalTypes
                        : undefined,
                plans:
                    tabFilters.plans && tabFilters.plans.length > 0
                        ? tabFilters.plans
                        : undefined,
                whoPays:
                    tabFilters.whoPays && tabFilters.whoPays.length > 0
                        ? tabFilters.whoPays
                        : undefined,
                services:
                    tabFilters.services && tabFilters.services.length > 0
                        ? tabFilters.services
                        : undefined,
            },
        }),
        [activeOrganization?.id, page, sort, filters, tabFilters],
    );

    const { data: slimData, isLoading: isSlimLoading } =
        useClientsCloseOverviewSlimQuery(slimParams);

    const workspaceIds = useMemo(
        () => slimData?.workspaces.map((w) => w.id) ?? [],
        [slimData],
    );

    const { data: detailsData } = useClientsCloseOverviewDetailsQuery(
        {
            organizationId: activeOrganization?.id ?? "",
            workspaceIds,
        },
        { enabled: workspaceIds.length > 0 },
    );

    const tableData = useMemo<ClientsCloseOverviewRow[] | null>(() => {
        if (isSlimLoading) {
            return null;
        }
        if (!slimData) {
            return [];
        }
        const detailsMap = new Map(
            (detailsData?.workspaces ?? []).map((ws) => [ws.id, ws]),
        );
        return slimData.workspaces.map((ws) => {
            const rawId = ws.id;
            // Details endpoint returns IDs in `workspace:<uuid>` format
            const details = detailsMap.get(`workspace:${rawId}`);
            if (details) {
                return mapWorkspaceToRow(details);
            }
            return {
                id: `workspace:${rawId}`,
                rowType: "workspace" as const,
                type: "workspace" as const,
                name: ws.name,
                workspaceId: rawId,
                teamMembers: [],
                accessMembers: [],
                team: [],
                members: [],
                invites: [],
                entities: [],
                children: [],
                bookkeepingStartDate: "",
                services: null,
            } satisfies ClientsCloseOverviewRow;
        });
    }, [isSlimLoading, slimData, detailsData]);

    const handleSearchChange = useCallback((search: string | undefined) => {
        setFilters((prev) => ({ ...prev, search: search ?? "" }));
        setPage(DEFAULT_PAGE);
    }, []);

    const handleRowClick = useCallback(
        (row: ClientsCloseOverviewRow | undefined) => {
            if (row?.workspaceId) {
                openClientWorkspace(row.workspaceId);
            }
        },
        [openClientWorkspace],
    );

    const tableRef = useRef<ViewTableHandle>(null);

    return (
        <article className={interfaceStyles.fullHeightContent}>
            <TablePageHeader
                search={filters.search}
                onSearchChange={handleSearchChange}
                filters={tabFilters}
                onFiltersChange={handleTabFiltersChange}
                availableFilters={availableFilters}
                isFullPage
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                {canCreateClient && <AddClientTrigger />}
            </TablePageHeader>

            <ViewTable
                ref={tableRef}
                data={tableData}
                view={view}
                config={clientsCloseOverviewViewConfig}
                expanded={expanded}
                onExpandedChange={setExpanded}
                onRowClick={handleRowClick}
                isParentSelectable
                className={styles.clientsTable}
                enableKeyboardShortcuts
                onViewChange={handleViewChange}
                fixedTopOffsetPx
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                footer={
                    slimData?.total ? (
                        <CommandIsland
                            sticky
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            <HStack gap={8}>
                                {slimData.pageCount > 1 && (
                                    <Pagination
                                        size="md"
                                        pageCount={slimData.pageCount}
                                        currentPage={page}
                                        onPageChange={setPage}
                                    />
                                )}
                                <CommandIslandTotal>
                                    {pluralize("Client", slimData.total)}:{" "}
                                    {slimData.total}
                                </CommandIslandTotal>
                            </HStack>
                        </CommandIsland>
                    ) : undefined
                }
            />
        </article>
    );
};

export const ClientsClosesPage: FC = () => (
    <ClientActionsProvider>
        <ClientsClosesPageContent />
    </ClientActionsProvider>
);
